import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import RecQuiz from '../RecQuizForm';
import QuizCarousel from '../QuizCarousel';
import { DEFAULT_QUESTIONS } from '../../../data/recquizdata';
import { getQuizQuestions, questionsForRoute, ROUTE_LABEL } from '../../../utils/recommendationQuiz';
import storageKeys from '../../constants/storageKeys';
import { useApp, useAuth } from '../../../context';
import { LoginForm } from '../Login';
import toast from 'react-hot-toast';

export default function RecommendationQuiz({ open, handleClick }) {
    const navigate = useNavigate();
    const { createItinerary } = useApp();
    const { user } = useAuth();
    const [items, setItems] = useState({});
    const [loading, setLoader] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState(false);

    // The quiz comes from Firestore (Admin → Recommendation Quiz). Seeded with
    // the bundled questions rather than an empty list so the dialog never
    // renders a step with nothing in it while the read is in flight — and so
    // it still works if the read fails.
    const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);

    // activeServiceProvider(provider, dispatch)
    const [quizId, setQuizId] = useState(0);

    useEffect(() => {
        let cancelled = false;
        getQuizQuestions().then((loaded) => {
            if (!cancelled) setQuestions(loaded);
        });
        return () => { cancelled = true; };
    }, []);

    /**
     * The route — `Day Activity` or `Holiday` — is the answer to the first
     * question, and it decides which of the remaining questions are asked.
     * Before it is answered the traveller sees every question, so the quiz has
     * a sensible length up front and shortens once they branch.
     */
    const route = items[ROUTE_LABEL];
    const steps = useMemo(() => questionsForRoute(questions, route), [questions, route]);

    // Changing the route mid-quiz can shorten the list under the current step.
    const currentId = Math.min(quizId, Math.max(steps.length - 1, 0));
    const current = steps[currentId];
    const isLastStep = currentId === steps.length - 1;

    const submitItinerary = async () => {
        setLoader(true);
        try {
            localStorage.setItem(storageKeys.VKCH_QUIZ, JSON.stringify(true));
            await createItinerary(items);
            toast.success("Adventure awaiting! Your personalized itinerary has been created successfully.");
            setQuizId(0);
            handleClick(false);
            navigate("/itinerary-list");
        } catch (error) {
            console.error("Itinerary creation error: ", error);
            toast.error("Something went wrong saving your itinerary. Please try again.");
        } finally {
            setLoader(false);
        }
    };

    const nextButton = () => {
        if (!isLastStep) {
            setQuizId(currentId + 1);
        } else {
            // Final step — gate behind authentication
            if (!user) {
                setPendingSubmission(true);
                setShowLogin(true);
                return;
            }
            submitItinerary();
        }
    };

    // Auto-resume after the user logs in while a submission is pending
    useEffect(() => {
        if (user && pendingSubmission) {
            setPendingSubmission(false);
            submitItinerary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, pendingSubmission]);

    const backButton = () => {
        if (currentId > 0) {
            setQuizId(currentId - 1)
        }
    }

    const addAnswers = (answers) => {
        setItems(answers)
    }

    return (
        <div>
            <Dialog
                open={open}
                // onClose intentionally omitted — Esc/backdrop dismissal is a flagged
                // behaviour item (see CHECKPOINT_REPORT); the X button closes the dialog.
                // Content-sized + centered at every breakpoint (no forced fullScreen): short
                // steps stay compact, the long interests step grows then scrolls inside the
                // paper (max-height 90vh). Near-full-width on mobile via small margins.
                fullWidth
                maxWidth="sm"
                aria-label="Recommendation quiz"
                sx={{
                    // `borderRadius` is intentionally omitted so the paper inherits the
                    // theme's MuiDialog radius (16px). NOTE: sx `borderRadius` is multiplied
                    // by theme.shape.borderRadius (12), so a numeric 3 would render 36px.
                    '& .MuiDialog-paper': {
                        m: { xs: 2, sm: 4 },
                        width: { xs: 'calc(100% - 32px)', sm: '100%' },
                        maxHeight: '90vh',
                    },
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        backgroundColor: 'background.paper',
                        position: 'relative'
                    }}
                >

                    <QuizCarousel data={current?.images || []} />
                    <IconButton
                        aria-label="cancel"
                        onClick={() => handleClick(false)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: '#fff',
                            backgroundColor: 'rgba(0, 0, 0, 0.45)',
                            zIndex: 999,
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent sx={{ padding: '12px 12px 0 12px', height: 'auto', width: '100%' }}>
                    {/* <Typography variant="h6">
                        For the best experience, tell us your preferences.
                    </Typography> */}
                    {current && <RecQuiz data={current} answers={items} addAnswers={addAnswers} />}
                </DialogContent>
                <DialogActions sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* <Button onClick={() => handleClick(false)} size="small">
                    Skip
                </Button> */}
                    <Button variant="text" color="inherit" onClick={backButton} disabled={currentId === 0} size="small">
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={nextButton}
                        size="small"
                        disabled={loading || !current}
                    >
                        {loading ? '' : isLastStep ? 'Create' : 'Next'}
                        {loading ? <CircularProgress style={{ color: 'inherit', margin: '6px' }} size={10} /> : ''}
                    </Button>
                </DialogActions>
            </Dialog>
            <LoginForm open={showLogin} setOpen={setShowLogin} />
        </div>
    );
}
